import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Server, Shield, Lock, Cloud, Database, Cpu } from "lucide-react";
import heroImage from "@/assets/hero-person-on-premise.png";
import { useLanguage } from "@/hooks/useLanguage";

const OnPremiseHero = () => {
  const { t, tArray, localizePath } = useLanguage();
  const trustBadges = tArray("onPremisePage.hero.trustBadges");

  return (
    <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 bg-gradient-to-b from-primary/5 to-background overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-8">
           <Link to={localizePath("/")} className="text-muted-foreground hover:text-foreground transition-colors">
             {t("onPremisePage.hero.home")}
           </Link>
           <span className="text-muted-foreground">/</span>
           <span className="text-muted-foreground">{t("onPremisePage.hero.products")}</span>
           <span className="text-muted-foreground">/</span>
           <span className="text-chathero-orange font-medium">{t("onPremisePage.hero.breadcrumbCurrent")}</span>
        </nav>

        <div className="grid lg:grid-cols-3 gap-12 items-center">
          {/* Text Content - 2/3 */}
          <div className="lg:col-span-2 text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight animate-fade-in">
              {t("onPremisePage.hero.title")}
            </h1>

            <p className="text-xl md:text-2xl text-chathero-orange font-semibold mb-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
              {t("onPremisePage.hero.subtitle")}
            </p>

            <p className="text-lg text-muted-foreground mb-10 max-w-3xl leading-relaxed animate-fade-in" style={{ animationDelay: "0.2s" }}>
              {t("onPremisePage.hero.description")}
            </p>

            <div className="flex flex-wrap gap-4 justify-center lg:justify-start animate-fade-in" style={{ animationDelay: "0.3s" }}>
              <Button size="lg" className="bg-chathero-mint text-accent-foreground hover:bg-chathero-mint/90 text-base px-8" asChild>
                <Link to={localizePath("/kontakt")}>{t("onPremisePage.hero.ctaPrimary")}</Link>
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start mt-8 animate-fade-in" style={{ animationDelay: "0.4s" }}>
              {trustBadges.map((badge) => (
                <span key={badge} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted/60 border border-border/50 rounded-full px-3 py-1.5">
                  <Shield className="w-3 h-3 text-chathero-mint" />
                  {badge}
                </span>
              ))}
            </div>
          </div>

          {/* Image - 1/3 */}
          <div className="relative flex justify-center lg:justify-end animate-scale-in" style={{ animationDelay: "0.2s" }}>
            {/* Floating Icons */}
            <div className="absolute top-[0%] left-[10%] z-10 animate-bounce" style={{ animationDuration: "3s" }}>
              <div className="w-12 h-12 rounded-2xl bg-chathero-orange/20 backdrop-blur-sm border border-chathero-orange/30 flex items-center justify-center shadow-lg">
                <Server className="w-6 h-6 text-chathero-orange" />
              </div>
            </div>
            <div className="absolute top-[0%] right-[10%] z-10 animate-bounce" style={{ animationDelay: "0.5s", animationDuration: "3.5s" }}>
              <div className="w-11 h-11 rounded-2xl bg-chathero-mint/20 backdrop-blur-sm border border-chathero-mint/30 flex items-center justify-center shadow-lg">
                <Shield className="w-5 h-5 text-chathero-mint" />
              </div>
            </div>
            <div className="absolute top-[30%] -left-[6%] z-10 animate-bounce" style={{ animationDelay: "1s", animationDuration: "4s" }}>
              <div className="w-10 h-10 rounded-2xl bg-primary/20 backdrop-blur-sm border border-primary/30 flex items-center justify-center shadow-lg">
                <Lock className="w-5 h-5 text-primary" />
              </div>
            </div>
            <div className="absolute top-[60%] -left-[4%] z-10 animate-bounce" style={{ animationDelay: "1.5s", animationDuration: "3.2s" }}>
              <div className="w-11 h-11 rounded-2xl bg-chathero-mint/20 backdrop-blur-sm border border-chathero-mint/30 flex items-center justify-center shadow-lg">
                <Database className="w-5 h-5 text-chathero-mint" />
              </div>
            </div>
            <div className="absolute bottom-[3%] left-[15%] z-10 animate-bounce" style={{ animationDelay: "0.7s", animationDuration: "3.8s" }}>
              <div className="w-10 h-10 rounded-2xl bg-chathero-orange/20 backdrop-blur-sm border border-chathero-orange/30 flex items-center justify-center shadow-lg">
                <Cloud className="w-5 h-5 text-chathero-orange" />
              </div>
            </div>
            <div className="absolute top-[25%] -right-[6%] z-10 animate-bounce" style={{ animationDelay: "2s", animationDuration: "3.6s" }}>
              <div className="w-10 h-10 rounded-2xl bg-primary/20 backdrop-blur-sm border border-primary/30 flex items-center justify-center shadow-lg">
                <Cpu className="w-5 h-5 text-primary" />
              </div>
            </div>

            <div className="relative w-full max-w-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-chathero-mint/20 to-primary/10 rounded-3xl blur-2xl" />
              <img
                src={heroImage}
                alt={t("onPremisePage.hero.breadcrumbCurrent")}
                className="relative z-[1] w-full object-contain"
              />

              {/* Mini-Dashboard Card Overlay */}
              <div className="absolute -right-10 sm:-right-16 bottom-[15%] z-20 animate-fade-in" style={{ animationDelay: "0.9s" }}>
                <div className="bg-background/90 backdrop-blur-md border border-border/60 rounded-2xl p-3 shadow-xl min-w-[160px]">
                  <p className="text-[10px] font-semibold text-primary mb-2">{t("onPremisePage.hero.statusTitle")}</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1.5">
                        <Server className="w-3 h-3 text-chathero-mint" />
                        <span className="text-[10px] text-muted-foreground">{t("onPremisePage.hero.statusEngine")}</span>
                      </div>
                      <span className="text-[11px] font-bold text-chathero-mint">{t("onPremisePage.hero.statusOnline")}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1.5">
                        <Database className="w-3 h-3 text-chathero-orange" />
                        <span className="text-[10px] text-muted-foreground">{t("onPremisePage.hero.statusKb")}</span>
                      </div>
                      <span className="text-[11px] font-bold text-chathero-orange">{t("onPremisePage.hero.statusKbValue")}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1.5">
                        <Shield className="w-3 h-3 text-primary" />
                        <span className="text-[10px] text-muted-foreground">{t("onPremisePage.hero.statusPrivacy")}</span>
                      </div>
                      <span className="text-[11px] font-bold text-primary">{t("onPremisePage.hero.statusPrivacyValue")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OnPremiseHero;
